import { Info } from "lucide-react";
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { pluralize } from "./contentSearchLabels";

interface ContentMatchIndicatorProps {
  /** Approximate occurrences of the search words in the text; 0 when the index kept no positions. */
  count: number;
  /** The count comes from an index that predates the current pipeline version. */
  indexStale?: boolean;
}

/**
 * The "Fandt X matches" pill DAK asked for (LAR-235): shown on a document that matches
 * in its text but not in its title, so the reader knows why it is in the list.
 */
export const ContentMatchIndicator = ({
  count,
  indexStale = false,
}: ContentMatchIndicatorProps) => {
  const label =
    count > 0
      ? `Fandt ${pluralize(count, "match", "matches")}`
      : "Match i indhold";
  const base =
    count > 0
      ? `Søgeordet findes ${pluralize(count, "sted", "steder")} i dokumentets indhold, men ikke i titlen. Åbn dokumentet for at springe mellem forekomsterne.`
      : "Søgeordet findes i dokumentets indhold, men ikke i titlen. Åbn dokumentet for at se forekomsterne.";
  const explanation = indexStale
    ? `${base} Søgeindekset er forældet, så antallet kan være for lavt.`
    : base;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="secondary"
          className="shrink-0 cursor-help gap-1 bg-primary/10 text-primary hover:bg-primary/15"
          aria-label={explanation}
        >
          <Info aria-hidden="true" />
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px]">
        <p className="text-xs leading-snug">{explanation}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ContentMatchIndicator;
